import { BufferGeometry, LineBasicMaterial, type Vector2 } from 'three';
import { markRaw } from 'vue';
import { groupNode, type GroupNode } from '@/lib/threeScene/nodes/group';
import { lineSegmentsNode, type LineSegmentsNode } from '@/lib/threeScene/nodes/lineSegments';
import type { NodeId } from '@/lib/threeScene/nodeId';
import type { StandardNode } from '@/lib/threeScene/updateNode';
import { tessellateDashedCircle } from '@/domain/geometry/tessellate';
import { vector3 } from '@/domain/geometry/vector3';

const DEFAULT_DASH_LENGTH = 4;
const DEFAULT_GAP_LENGTH = 4;
const DEFAULT_SEGMENTS = 64;

export type FemurHeadCircleResult = {
    sceneGroup: GroupNode;
    children: StandardNode[];
};

export type FemurHeadCircleArgs = {
    head: 1 | 2;
    parentId: NodeId;
    center: Vector2;
    radius: number;
    colour: string;
};

/**
 * Build a dashed-circle group representing the femur head, parented to the
 * femoral annotation group. Reuses tessellateDashedCircle for vertex generation.
 */
export function buildFemurHeadCircleGroup(args: FemurHeadCircleArgs): FemurHeadCircleResult {
    const groupId = `group-femur-head-${args.head}`;
    const sceneGroup = groupNode(groupId, { parent: args.parentId });

    const verts = tessellateDashedCircle(
        {
            center: vector3(args.center.x, args.center.y, 0),
            radius: args.radius,
            dashLength: DEFAULT_DASH_LENGTH,
            gapLength: DEFAULT_GAP_LENGTH,
        },
        DEFAULT_SEGMENTS,
    );

    const geometry = markRaw(new BufferGeometry().setFromPoints(verts));
    const material = markRaw(new LineBasicMaterial({ color: args.colour, depthTest: false }));

    const lineNode = lineSegmentsNode(`line-femur-head-${args.head}`, {
        parent: groupId,
        geometry,
        material,
    });

    return { sceneGroup, children: [lineNode] };
}

/**
 * Rebuild the dashed-circle geometry on an existing femur-head line node.
 * Disposes the previous BufferGeometry and replaces it.
 */
export function rebuildFemurHeadCircleGeometry(
    lineNode: LineSegmentsNode,
    center: Vector2,
    radius: number,
): void {
    const verts = tessellateDashedCircle(
        {
            center: vector3(center.x, center.y, 0),
            radius,
            dashLength: DEFAULT_DASH_LENGTH,
            gapLength: DEFAULT_GAP_LENGTH,
        },
        DEFAULT_SEGMENTS,
    );
    lineNode.geometry?.dispose();
    lineNode.geometry = markRaw(new BufferGeometry().setFromPoints(verts));
}
